import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAppSettings } from '../context/AppSettingsContext';
import SettingsPanel from './SettingsPanel';
import { useLocation } from 'react-router-dom';
import MenuDrawer from './MenuDrawer';

const Home: React.FC = () => {
  const { settings } = useAppSettings();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showDebug = queryParams.get('debug') === 'true';

  const [markdown, setMarkdown] = useState<string>('Loading...');
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);

  useEffect(() => {
    const fetchTest = async () => {
      setIsLoading(true);
      setHasError(false);
      const url = [
        'https://raw.githubusercontent.com',
        settings.githubUser,
        settings.consciousness,
        settings.neuralpathway,
        settings.basePath,
        'cosmos-logos.md'
      ]
        .map(part => part.replace(/^\/|\/$/g, ''))
        .join('/');

      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);

        const text = await res.text();
        const stripped = text.replace(/^---[\s\S]*?---\s*/, '');
        setMarkdown(stripped);
        setIsConnected(true);
      } catch (err) {
        setMarkdown('');
        setIsConnected(false);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTest();
  }, [settings]);

  return (
    <>
      <MenuDrawer />
      <main className="odyssey-press-home" style={{ position: 'relative' }}>
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 1000 }}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            style={{
              background: '#222',
              color: '#ffdd80',
              border: '1px solid #444',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '1.2rem',
              cursor: 'pointer',
            }}
          >
            ☰
          </button>
        </div>

        {showMenu && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              height: '100vh',
              width: '320px',
              backgroundColor: '#1a1a1a',
              color: '#fff',
              borderLeft: '1px solid #444',
              padding: '1rem',
              zIndex: 999,
              overflowY: 'auto',
            }}
          >
            <h3 style={{ color: '#ffdd80' }}>Menu</h3>
            <SettingsPanel />
          </div>
        )}

        <div className="hero">
          <h1>
            <span className="brand">Cosmos-Logos</span>
          </h1>
        </div>

        <div
          style={{
            marginTop: '4rem',
            padding: '1.5rem',
            backgroundColor: '#1c1c2b',
            borderRadius: '8px',
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <h2 style={{ color: '#80ffb0', fontSize: '1.25rem', marginBottom: '1rem' }}>
            ✅ cosmos-logos.md Check
          </h2>

          <div
            style={{
              backgroundColor: '#111',
              border: '1px solid #444',
              borderRadius: '6px',
              padding: '1rem',
              fontFamily: 'ui-monospace, SFMono-Regular, Consolas, Liberation Mono, Menlo, monospace',
              color: '#ccc',
              fontSize: '0.9rem',
              whiteSpace: 'pre-wrap',
            }}
          >
            <ReactMarkdown
              skipHtml={true}
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <p style={{ marginBottom: '1rem' }}>{children}</p>,
              }}
            >
              {markdown}
            </ReactMarkdown>
          </div>

          <div
            style={{
              marginTop: '2rem',
              backgroundColor: '#111',
              padding: '1rem',
              borderRadius: '6px',
              border: '1px solid #444',
              color: '#ccc',
              fontSize: '0.95rem',
            }}
          >
            <h3
              style={{
                color: isLoading
                  ? '#ffdd80'
                  : isConnected
                  ? '#80ffb0'
                  : hasError
                  ? '#ff6b6b'
                  : '#ccc',
                fontWeight: 'bold',
                marginBottom: '0.75rem',
              }}
            >
              {isLoading
                ? '⌛ Connecting to Cosmos-Logos...'
                : isConnected
                ? '✅ Connected to Cosmos-Logos'
                : hasError
                ? '❌ cosmos-logos.md not found'
                : ''}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div>
                <strong>GitHub Account:</strong> <code>{settings.githubUser}</code>
              </div>
              <div>
                <strong>Consciousness (Repo):</strong> <code>{settings.consciousness}</code>
              </div>
              <div>
                <strong>Neural Pathway (Branch):</strong> <code>{settings.neuralpathway}</code>
              </div>
              <div>
                <strong>Source of Truth:</strong> <code>{settings.basePath}</code>
              </div>
            </div>
          </div>

          {showDebug && (
            <div
              style={{
                background: '#111',
                border: '1px dashed #555',
                borderRadius: '6px',
                padding: '1rem',
                marginTop: '3rem',
                fontSize: '0.85rem',
                color: '#ccc',
                whiteSpace: 'pre-wrap',
              }}
            >
              <h4 style={{ marginBottom: '0.5rem', color: '#ffdd80' }}>🛠️ Debug Panel</h4>

              <div style={{ marginBottom: '1rem' }}>
                <strong>Runtime Settings</strong>
                <pre>{JSON.stringify(settings, null, 2)}</pre>
              </div>

              <div>
                <strong>Markdown Content</strong>
                <pre>{markdown}</pre>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default Home;
